if (!customElements.get('pair-bundle')) {
  customElements.define('pair-bundle', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) {
        this.scope = this.closest('[data-testid="product-information-details"]');
        this.scope?.classList.add('pair-bundle-ready');
        this.activateBridge();
        return;
      }
      try {
        this.product = JSON.parse(this.querySelector('[data-pair-product]').textContent);
        this.variants = this.product.variants || [];
        this.offers = [...this.querySelectorAll('[data-pair-offer]')];
        if (!this.offers.length || !this.variants.some(v => v.available && Number(v.price) > 0)) return;
        this.initialized = true;
        this.cartModules = import('@shopify/events').catch(() => null);
        this.status = this.querySelector('[data-pair-status]');
        this.submit = this.querySelector('[data-pair-submit]');
        this.submitLabel = this.submit.textContent;
        this.scope = this.closest('[data-testid="product-information-details"]');
        this.status.id ||= `pair-status-${globalThis.crypto?.randomUUID?.() || Math.random().toString(36).slice(2)}`;
        this.money = new Intl.NumberFormat(this.dataset.locale || 'fr', { style: 'currency', currency: this.dataset.currency || 'EUR' });
        const key = `pair-offer-${globalThis.crypto?.randomUUID?.() || Math.random().toString(36).slice(2)}`;
        this.offers.forEach((offer, offerIndex) => {
          const radio = offer.querySelector('[data-offer-radio]');
          radio.name = key;
          const panel = offer.querySelector('[data-offer-panel]');
          panel.id = `${key}-${offerIndex}`;
          radio.setAttribute('aria-controls', panel.id);
          this.buildChoices(offer);
          radio.addEventListener('change', () => this.choose(offer));
        });
        this.submit.addEventListener('click', () => this.add());
        this.addEventListener('shopify:block:select', event => {
          const offer = event.target.closest('[data-pair-offer]');
          if (offer && this.offers.includes(offer)) this.choose(offer);
        });
        const requested = new URLSearchParams(window.location.search).get('bundle');
        this.choose(this.offers.find(offer => offer.dataset.quantity === (requested || this.dataset.defaultQuantity)) || this.offers[0]);
        this.scope = this.closest('[data-testid="product-information-details"]');
        this.scope?.classList.add('pair-bundle-ready');
        this.activateBridge();
      } catch (_) {
        this.querySelector('[data-pair-status]').textContent = 'Utilisez les choix du produit pour commander.';
      }
    }
    disconnectedCallback() {
      this.scope?.classList.remove('pair-bundle-ready');
      this.bridgeEvents?.abort();
      this.bridgeObserver?.disconnect();
      this.releaseConversion();
    }
    // Bind only the Conversion CTA for the same product in this product-details area.
    activateBridge() {
      this.bridgeEvents?.abort();
      this.bridgeObserver?.disconnect();
      if (!this.scope || this.scope.querySelector('pair-bundle') !== this) return;
      this.bridgeEvents = new AbortController();
      const signal = this.bridgeEvents.signal;
      this.scope.addEventListener('click', event => {
        const target = event.target instanceof Element ? event.target.closest('[data-orelven-submit]') : null;
        if (!this.isConnected || target !== this.conversionButton) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        if (!target.disabled && target.getAttribute('aria-disabled') !== 'true') this.add();
      }, { capture: true, signal });
      this.scope.addEventListener('submit', event => {
        if (!this.isConnected || !this.conversionButton || event.target !== this.conversionButton.form) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        if (!this.conversionButton.disabled) this.add();
      }, { capture: true, signal });
      this.bindConversion();
      // Theme-editor replacement of a Conversion block must reconnect without a page reload.
      this.bridgeObserver = new MutationObserver(() => this.bindConversion());
      this.bridgeObserver.observe(this.scope, { childList: true, subtree: true });
    }
    bindConversion() {
      const candidates = [...this.scope.querySelectorAll('[data-orelven-submit]')];
      const button = candidates.find(button =>
        button.closest('product-form-component')?.dataset.productId === String(this.product.id)
      );
      if (button === this.conversionButton) return;
      this.releaseConversion();
      if (!button) return;
      this.conversionButton = button;
      this.conversionOriginal = {
        disabled: button.disabled,
        ariaDisabled: button.getAttribute('aria-disabled'),
        describedBy: button.getAttribute('aria-describedby'),
        label: button.getAttribute('aria-label'),
        labelText: button.querySelector('[data-orelven-button-label]')?.textContent
      };
      button.setAttribute('aria-describedby', [this.conversionOriginal.describedBy, this.status.id].filter(Boolean).join(' '));
      this.classList.add('pair-bundle--linked');
      this.syncConversion();
    }
    releaseConversion() {
      if (this.conversionButton && this.conversionOriginal) {
        const button = this.conversionButton;
        button.disabled = this.conversionOriginal.disabled;
        for (const [attribute, key] of [['aria-disabled','ariaDisabled'], ['aria-describedby','describedBy'], ['aria-label','label']]) {
          const value = this.conversionOriginal[key];
          if (value === null) button.removeAttribute(attribute); else button.setAttribute(attribute, value);
        }
        button.removeAttribute('aria-busy');
        const label = button.querySelector('[data-orelven-button-label]');
        if (label && this.conversionOriginal.labelText !== undefined) label.textContent = this.conversionOriginal.labelText;
      }
      this.conversionButton = null;
      this.conversionOriginal = null;
      this.classList.remove('pair-bundle--linked');
    }
    unitLabel(quantity) {
      return Number(quantity) === 1 ? (this.dataset.unitSingular || 'paire') : (this.dataset.unitPlural || 'paires');
    }
    syncConversion() {
      const cartOnly = Boolean(this.reviewState || this.existingCartReadOnly);
      if (this.submit) {
        this.submit.disabled = Boolean(this.pending || (!cartOnly && this.active?.dataset.valid !== 'true'));
        this.submit.textContent = cartOnly ? 'Voir mon panier' : this.submitLabel;
      }
      const button = this.conversionButton;
      if (!button) return;
      button.disabled = Boolean(this.pending || (!cartOnly && this.active?.dataset.valid !== 'true'));
      button.setAttribute('aria-disabled', String(button.disabled));
      if (this.pending) button.setAttribute('aria-busy', 'true'); else button.removeAttribute('aria-busy');
      const labelNode = button.querySelector('[data-orelven-button-label]');
      if (labelNode && this.conversionOriginal.labelText !== undefined) labelNode.textContent = cartOnly ? 'Voir mon panier' : this.conversionOriginal.labelText;
      const label = labelNode?.textContent.trim() || button.textContent.trim();
      button.setAttribute('aria-label', cartOnly ? label : `${label} — lot de ${this.active?.dataset.quantity || ''} ${this.unitLabel(this.active?.dataset.quantity)}`);
    }
    buildChoices(offer) {
      const root = offer.querySelector('[data-pair-choices]');
      root.replaceChildren();
      const count = Number(offer.dataset.quantity);
      if (!Number.isInteger(count) || count < 1 || count > 10) return;
      const initial = this.variants.find(v => v.available && Number(v.price) > 0);
      const swatches = new Map();
      (this.dataset.swatchMap || '').split(/\r?\n/).forEach(line => {
        const separator = line.indexOf(':');
        if (separator < 1) return;
        const name = line.slice(0, separator).trim().toLocaleLowerCase();
        const color = line.slice(separator + 1).trim();
        if (/^#[0-9a-f]{3,8}$/i.test(color) && [4,5,7,9].includes(color.length)) swatches.set(name, color);
      });
      for (let pair = 0; pair < count; pair++) {
        const row = document.createElement('fieldset');
        row.className = 'pair-choice';
        const legend = document.createElement('legend');
        legend.className = 'visually-hidden';
        legend.textContent = `${this.unitLabel(1)} ${pair + 1}`;
        const number = document.createElement('span');
        number.className = 'pair-choice__number';
        number.setAttribute('aria-hidden', 'true');
        number.textContent = `#${pair + 1}`;
        row.append(legend, number);
        this.product.options.forEach((option, position) => {
          const name = typeof option === 'string' ? option : option.name;
          const isColor = /^(couleur|colou?r)$/i.test(name);
          const label = document.createElement('label');
          label.className = 'pair-choice__option';
          const labelText = document.createElement('span');
          labelText.className = 'visually-hidden';
          labelText.textContent = `${this.unitLabel(1)} ${pair + 1} — ${name}`;
          const control = document.createElement('span');
          control.className = 'pair-choice__control';
          const select = document.createElement('select');
          select.dataset.position = position;
          select.setAttribute('aria-label', `${this.unitLabel(1)} ${pair + 1} — ${name}`);
          const values = [...new Set(this.variants.map(v => v.options[position]))];
          values.forEach(value => {
            const item = document.createElement('option');
            item.value = value;
            item.textContent = value;
            select.append(item);
          });
          select.value = initial.options[position];
          const swatch = document.createElement('span');
          swatch.className = 'pair-choice__swatch';
          swatch.setAttribute('aria-hidden', 'true');
          const updateSwatch = () => {
            const color = swatches.get(select.value.toLocaleLowerCase());
            const show = isColor && this.dataset.showSwatches === 'true' && Boolean(color);
            swatch.hidden = !show;
            control.classList.toggle('has-swatch', show);
            if (show) swatch.style.backgroundColor = color;
          };
          updateSwatch();
          select.addEventListener('change', () => { updateSwatch(); this.refresh(offer); });
          control.append(select, swatch);
          label.append(labelText, control);
          row.append(label);
        });
        if (this.dataset.defaultVariant === 'true') row.hidden = true;
        root.append(row);
      }
      this.refresh(offer);
    }
    selection(offer) {
      return [...offer.querySelectorAll('.pair-choice')].map(row => {
        const options = [...row.querySelectorAll('select')].map(select => select.value);
        return this.variants.find(variant => variant.options.every((value, position) => value === options[position]));
      });
    }
    offerPrice(offer) {
      const raw = (offer.dataset.offerPrice || '').trim().replace(/\s/g, '').replace(',', '.');
      if (!raw) return null;
      if (!/^\d+(\.\d{1,2})?$/.test(raw)) return NaN;
      const cents = Math.round(Number(raw) * 100);
      return Number.isSafeInteger(cents) && cents > 0 ? cents : NaN;
    }
    refresh(offer) {
      const variants = this.selection(offer);
      const count = Number(offer.dataset.quantity);
      const valid = variants.length === count && variants.every(v => v && v.available && Number(v.price) > 0);
      const manualPrice = this.offerPrice(offer);
      const sum = variants.reduce((total, variant) => total + Number(variant?.price || 0), 0);
      const hasManualPrice = Number.isFinite(manualPrice) && manualPrice > 0;
      const priceConfigured = manualPrice === null || (hasManualPrice && (manualPrice === sum || (offer.dataset.discountVerified === 'true' && Boolean(offer.dataset.discount.trim()))));
      const displayed = Number.isFinite(manualPrice) && manualPrice > 0 ? manualPrice : sum;
      offer.querySelector('[data-offer-total]').textContent = !valid ? 'Indisponible' : manualPrice !== null && !hasManualPrice ? 'Prix à renseigner' : this.money.format(displayed / 100);
      offer.querySelector('[data-offer-unit]').textContent = valid && (manualPrice === null || hasManualPrice) ? (offer.dataset.unitMode === 'custom' ? (offer.dataset.unitText || '') : `${this.money.format(displayed / count / 100)} ${this.dataset.unitSuffix || ""}`.trim()) : '';
      const compareRaw = (offer.dataset.comparePrice || '').trim().replace(',', '.');
      const compareValue = /^\d+(\.\d{1,2})?$/.test(compareRaw) ? Math.round(Number(compareRaw) * 100) : null;
      const hasCompare = Number.isFinite(compareValue) && compareValue > 0;
      const reference = hasCompare ? compareValue : sum;
      const saving = valid && (manualPrice === null || hasManualPrice) && reference > displayed && (hasCompare || manualPrice !== null);
      const original = offer.querySelector('[data-offer-original]');
      original.hidden = !saving;
      original.textContent = saving ? this.money.format(reference / 100) : '';
      const savingText = offer.querySelector('[data-offer-saving]');
      savingText.hidden = !saving;
      savingText.textContent = saving ? `${this.dataset.savingLabel || ""} ${this.money.format((reference - displayed) / 100)}`.trim() : '';
      offer.dataset.valid = String(valid && priceConfigured);
      if (offer === this.active && !this.pending) {
        this.submit.disabled = !(valid && priceConfigured);
        this.syncConversion();
        this.status.textContent = this.reviewState?.error || (!valid ? 'Cette combinaison n’est pas disponible. Choisissez une autre couleur ou taille.' : !priceConfigured ? 'Cette offre attend la confirmation de son prix Shopify. Choisissez une autre offre.' : (this.cartError || ''));
      }
    }
    choose(offer) {
      if (this.pending) return;
      if (!this.reviewState) this.cartError = '';
      this.active = offer;
      this.offers.forEach(item => {
        const active = item === offer;
        item.classList.toggle('is-selected', active);
        item.querySelector('[data-offer-radio]').checked = active;
        item.querySelector('[data-offer-radio]').setAttribute('aria-expanded', String(active));
        item.querySelector('[data-offer-panel]').hidden = !active;
      });
      this.refresh(offer);
      this.dispatchEvent(new CustomEvent('orelven:bundle-change',{bubbles:true,detail:{quantity:offer.dataset.quantity}}));
    }
    cartSectionIds() {
      return [...new Set([...document.querySelectorAll('cart-items-component')].map(component => component.dataset.sectionId).filter(Boolean))].slice(0, 5);
    }
    async openNativeCart(cart, lines, items, action = 'add') {
      let observer, timer, moduleTimer, stage = 'drawer';
      try {
        const drawer = document.querySelector('cart-drawer-component');
        if (!drawer || !drawer.closest('theme-drawer')) throw new Error('Native cart drawer missing');
        stage = 'modules';
        const [events] = await Promise.race([
          Promise.all([this.cartModules || import('@shopify/events'), customElements.whenDefined('cart-items-component'), customElements.whenDefined('cart-drawer-component'), customElements.whenDefined('theme-drawer')]),
          new Promise((_, reject) => { moduleTimer = setTimeout(() => reject(new Error('Cart components unavailable')), 3000); })
        ]);
        clearTimeout(moduleTimer);
        if (!events) throw new Error('Cart events unavailable');
        const { CartLinesUpdateEvent } = events;
        stage = 'section-ids';
        const sectionIds = this.cartSectionIds();
        const drawerSectionId = drawer.querySelector('cart-items-component')?.dataset.sectionId;
        if (!drawerSectionId || !sectionIds.length) throw new Error('Cart section id missing');
        stage = 'sections-fetch';
        let renderedCart = cart;
        if (!cart.sections?.[drawerSectionId]) {
          const response = await fetch(this.dataset.updateUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ sections: sectionIds.join(','), sections_url: window.location.pathname }),
          signal: AbortSignal.timeout(5000)
          });
          if (!response.ok) throw new Error(`Section rendering HTTP ${response.status}`);
          renderedCart = await response.json();
        }
        const sections = renderedCart.sections;
        if (!sections?.[drawerSectionId]) throw new Error(`Rendered cart section missing: ${drawerSectionId}`);
        stage = 'cart-event';
        const reflected = new Promise(resolve => {
          const check = () => {
            const current = document.querySelector('cart-drawer-component');
            const keys = new Set([...(current?.querySelectorAll('[data-key]') || [])].map(row => row.dataset.key));
            if (lines.every(line => keys.has(line.key))) resolve(true);
          };
          observer = new MutationObserver(check);
          observer.observe(document.body, { childList: true, subtree: true });
          timer = setTimeout(() => resolve(false), 3500);
          check();
        });
        const deferred = CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(new CartLinesUpdateEvent({ action, context: action === 'add' ? 'product' : 'cart', lines: action === 'add' ? items.map(item => ({ merchandiseId: String(item.id), quantity: item.quantity })) : lines.map(line => ({ id: line.key, quantity: line.quantity })), promise: deferred.promise }));
        deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(renderedCart), detail: { items: renderedCart.items, source: 'pair-bundle', sourceId: this.id || 'pair-bundle', itemCount: lines.reduce((sum, line) => sum + Number(line.quantity), 0), productId: String(this.product.id), sections, didError: false } });
        stage = 'morph';
        if (!await reflected) throw new Error('New cart lines were not rendered');
        stage = 'open';
        const nativeDrawer = document.querySelector('cart-drawer-component')?.closest('theme-drawer');
        if (typeof nativeDrawer?.open !== 'function') throw new Error('Native drawer open method missing');
        if (!nativeDrawer.isOpen) await nativeDrawer.open();
        await new Promise(resolve => requestAnimationFrame(resolve));
        if (!nativeDrawer.isOpen && !nativeDrawer.hasAttribute('open')) throw new Error('Native drawer remained closed');
        delete this.dataset.cartDrawerFallback;
        return true;
      } catch (error) {
        this.dataset.cartDrawerFallback = stage;
        console.warn(`[pair-bundle] Cart drawer fallback (${stage}):`, error?.message || String(error));
        return false;
      } finally {
        observer?.disconnect();
        clearTimeout(timer);
        clearTimeout(moduleTimer);
      }
    }
    async request(url, body) {
      const response = await fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) });
      const data = await response.json();
      if (!response.ok || data.status >= 400) throw new Error(typeof data.description === 'string' ? data.description : 'Le panier n’a pas pu être mis à jour. Vérifiez les disponibilités puis réessayez.');
      return data;
    }
    canReplaceCurcuma(lines) {
      const variant = this.variants?.[0];
      return String(this.product?.id) === '15929646121300' && this.variants?.length === 1 && lines.length === 1 && !lines[0].selling_plan_allocation && !lines[0].selling_plan && Boolean(lines[0].key) && String(lines[0].variant_id || lines[0].id) === String(variant.id) && Number.isSafeInteger(Number(lines[0].quantity)) && Number(lines[0].quantity) > 0;
    }
    async readCart() {
      const response = await fetch(`${this.dataset.cartUrl}.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' });
      if (!response.ok) throw new Error('Impossible d’actualiser le panier. Réessayez.');
      const cart = await response.json();
      if (!Array.isArray(cart.items) || !this.product?.id || cart.items.some(line => !line.product_id)) throw new Error('Impossible d’actualiser le panier. Réessayez.');
      return cart;
    }
    discountState(cart) {
      const normalize = value => String(value || '').trim().toLocaleLowerCase();
      const own = new Set(this.offers.filter(offer => offer.dataset.discountVerified === 'true').map(offer => normalize(offer.dataset.discount)).filter(Boolean));
      const codes = Array.isArray(cart.discount_codes) ? cart.discount_codes : [];
      const applicable = codes.filter(entry => entry.applicable !== false);
      const allApplicableOwn = applicable.length > 0 && applicable.every(entry => own.has(normalize(entry.code)));
      const applications = [
        ...(cart.cart_level_discount_applications || []),
        ...(cart.discount_applications || []),
        ...cart.items.flatMap(line => (line.line_level_discount_allocations || []).map(allocation => allocation.discount_application))
      ];
      const recognized = application => {
        if (!application) return false;
        if (application.code) return own.has(normalize(application.code));
        if (application.type && application.type !== 'discount_code') return false;
        if (own.has(normalize(application.title))) return true;
        return application.type === 'discount_code' && allApplicableOwn;
      };
      const ownTitles = new Set(applications.filter(recognized).map(application => normalize(application.title)).filter(Boolean));
      const foreign = applicable.some(entry => !own.has(normalize(entry.code))) || applications.some(application => !recognized(application)) || cart.items.some(line => (line.discounts || []).some(application => !recognized(application) && !(!application?.type && !application?.code && ownTitles.has(normalize(application?.title)))));
      // Retain inactive foreign codes too: adding this offer must not silently remove a customer's coupon.
      const preserve = [...new Set(codes.filter(entry => !own.has(normalize(entry.code))).map(entry => String(entry.code || '').trim()).filter(Boolean))];
      const ownCodePresent = codes.some(entry => own.has(normalize(entry.code)));
      return { foreign, preserve, ownCodePresent };
    }
    verifyLines(cart, lines, quantity, expected) {
      if (!lines.length || lines.some(line => !Number.isSafeInteger(Number(line.quantity)) || Number(line.quantity) < 1) || lines.reduce((sum, line) => sum + Number(line.quantity), 0) !== quantity) throw new Error('La quantité ajoutée ne correspond pas au lot choisi.');
      if (expected !== null && (!Array.isArray(cart.cart_level_discount_applications) || cart.cart_level_discount_applications.length > 0)) throw new Error('Cette offre ne se cumule pas avec la remise déjà appliquée.');
      const total = lines.reduce((sum, line) => sum + Number(line.final_line_price), 0);
      if (lines.some(line => !Number.isSafeInteger(Number(line.final_line_price)) || Number(line.final_line_price) < 0) || !Number.isSafeInteger(total) || (expected !== null && (!Number.isSafeInteger(expected) || total !== expected))) throw new Error('Le montant du panier doit être vérifié.');
    }
    reconcileCart(cart) {
      const lines = cart.items.filter(line => String(line.product_id) === String(this.product.id));
      if (this.reviewState) {
        const reviewed = lines.filter(line => this.reviewState.token ? line.properties?._pair_bundle === this.reviewState.token : line.key === this.reviewState.lineKey);
        if (!reviewed.length) this.reviewState = null;
        else {
          try {
            this.verifyLines(cart, reviewed, this.reviewState.quantity, this.reviewState.expected);
            this.reviewState = null;
          } catch (error) {
            this.reviewState.error = error.message;
          }
        }
      }
      this.existingCartKnown = lines.length > 0;
      this.existingCartReadOnly = lines.length > 0 && !this.canReplaceCurcuma(lines);
      this.cartError = '';
      this.status.textContent = this.reviewState?.error || '';
      const cartLink = this.querySelector('[data-pair-cart-link]');
      if (cartLink) cartLink.hidden = !this.reviewState;
      this.syncConversion();
      return lines;
    }
    async showExistingCart(cart, lines) {
      if (!await this.openNativeCart(cart, lines, [], 'update')) window.location.assign(this.dataset.cartUrl);
    }
    async add() {
      if (this.pending || !this.active) return;
      const viewRequested = Boolean(this.reviewState || this.existingCartReadOnly);
      this.refresh(this.active);
      if (!viewRequested && this.active.dataset.valid !== 'true') return;
      const offer = this.active;
      const variants = this.selection(offer).filter(Boolean);
      const token = globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`;
      const expected = this.offerPrice(offer);
      const quantity = Number(offer.dataset.quantity);
      const grouped = new Map();
      variants.forEach(variant => grouped.set(variant.id, (grouped.get(variant.id) || 0) + 1));
      const offerLabel = offer.querySelector('.pair-offer__title')?.textContent.trim() || '';
      const items = [...grouped].map(([id, quantity]) => ({ id, quantity, properties: { _pair_bundle: token, _pair_bundle_offer: offerLabel, _pair_bundle_discount: offer.dataset.discount.trim() } }));
      this.pending = true;
      this.setAttribute('aria-busy', 'true');
      this.syncConversion();
      this.offers.forEach(item => item.querySelectorAll('input, select').forEach(input => { input.disabled = true; }));
      let addAttempted = false;
      let added = false;
      let priceVerified = false;
      let replacing = false;
      try {
        // Read before EVERY offer, including one unit. A fixed order code cannot price two independent lots.
        const existing = await this.readCart();
        const existingLines = this.reconcileCart(existing);
        if (existingLines.length && (viewRequested || !this.canReplaceCurcuma(existingLines))) {
          await this.showExistingCart(existing, existingLines);
          return;
        }
        if (existingLines.length && Number(existingLines[0].quantity) === quantity) {
          try {
            this.verifyLines(existing, existingLines, quantity, expected);
          } catch (error) {
            this.reviewState = { token: existingLines[0].properties?._pair_bundle || null, lineKey: existingLines[0].key, quantity, expected, error: error.message };
            throw error;
          }
          await this.showExistingCart(existing, existingLines);
          return;
        }
        if (viewRequested) {
          // This click promised to view the cart. After removal, a separate intentional click may add again.
          window.location.assign(this.dataset.cartUrl);
          return;
        }
        if (this.active.dataset.valid !== 'true') return;
        const catalogueTotal = variants.reduce((sum, variant) => sum + Number(variant.price), 0);
        const discount = expected !== null && expected < catalogueTotal && offer.dataset.discountVerified === 'true' ? offer.dataset.discount.trim() : '';
        const discounts = this.discountState(existing);
        replacing = existingLines.length > 0;
        if ((discount || replacing) && discounts.foreign) throw new Error('Une autre remise est déjà appliquée dans votre panier.');
        addAttempted = true;
        if (replacing) {
          const line = existingLines[0];
          await this.request(`${this.dataset.cartUrl}/change.js`, { id: line.key, quantity, properties: { ...(line.properties || {}), ...items[0].properties } });
        } else {
          await this.request(this.dataset.addUrl, { items });
        }
        added = true;
        const sectionIds = this.cartSectionIds();
        const cart = await this.request(this.dataset.updateUrl, {
          ...(discount || discounts.ownCodePresent ? { discount: [...discounts.preserve, ...(discount ? [discount] : [])].join(',') } : {}),
          ...(sectionIds.length ? { sections: sectionIds.join(','), sections_url: window.location.pathname } : {})
        });
        if (!Array.isArray(cart.items)) throw new Error('Le montant du panier doit être vérifié.');
        const lines = cart.items.filter(line => line.properties?._pair_bundle === token);
        this.verifyLines(cart, lines, quantity, expected);
        priceVerified = true;
        this.cartError = '';
        this.reviewState = null;
        this.existingCartKnown = true;
        this.existingCartReadOnly = !this.canReplaceCurcuma(lines);
        this.status.textContent = '';
        if (!await this.openNativeCart(cart, lines, items, replacing ? 'update' : 'add')) window.location.assign(this.dataset.cartUrl);
      } catch (error) {
        if (addAttempted && !added) {
          try {
            const current = await this.readCart();
            added = current.items.some(line => line.properties?._pair_bundle === token);
          } catch (_) {
            added = true; // The response is unknown; a second click must inspect the cart, never blindly add again.
          }
        }
        const message = error instanceof TypeError || error.name === 'AbortError' ? 'Impossible d’actualiser le panier. Réessayez.' : error.message;
        console.warn('[pair-bundle] Add not completed:', error.message);
        if (added && !priceVerified) this.reviewState = { token, quantity, expected, error: message };
        this.cartError = priceVerified ? '' : message;
        this.status.textContent = this.reviewState?.error || this.cartError;
        const cartLink = this.querySelector('[data-pair-cart-link]');
        if (cartLink && this.reviewState) cartLink.hidden = false;
        if (priceVerified || (viewRequested && !addAttempted)) window.location.assign(this.dataset.cartUrl);
      } finally {
        this.pending = false;
        this.removeAttribute('aria-busy');
        this.offers.forEach(item => item.querySelectorAll('input, select').forEach(input => { input.disabled = false; }));
        this.syncConversion();
      }
    }
  });
}
